import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { listingsService } from '../../api/services/listingsService';
import { chatService } from '../../api/services/chatService';

export interface ListingImage {
  id: string;
  url: string;
  isPrimary: boolean;
  alt: string;
}

export interface SimilarListing {
  id: string;
  slug: string | null;
  title: string;
  make: string;
  model: string;
  year: number;
  price: number | null;
  mileage: number | null;
  fuelType: string | null;
  transmission: string | null;
  location: string | null;
  city: string | null;
  priceTag: 'GOOD_PRICE' | 'FAIR_PRICE' | 'HIGH_PRICE' | null;
  image: string | null;
}

export interface ListingDetailsData {
  id: string;
  slug: string | null;
  title: string;
  make: string;
  model: string;
  year: number;
  price: number;
  originalPrice: number | null;
  mileage: number;
  fuelType: string | null;
  transmission: string | null;
  bodyType: string | null;
  condition: string | null;
  color: string | null;
  doors: number | null;
  seats: number | null;
  isNegotiable: boolean;
  location: string;
  city: string | null;
  country: string | null;
  description: string;
  features: string[];
  category: string;
  images: ListingImage[];
  user: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    whatsapp: string | null;
  };
  dealer: {
    id: string;
    companyName: string;
    city: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  status: string;
  viewCount: number;
  favoriteCount: number;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
  rankingScore?: number | null;
  priceTag?: 'GOOD_PRICE' | 'FAIR_PRICE' | 'HIGH_PRICE' | null;
  equipment?: Record<string, any> | null;
  contact?: { telephoneNumber: string | null; displayPhoneNumber: boolean; showWhatsApp: boolean } | null;
  isImported: boolean;
  importCountry: string | null;
  shippingIncluded: boolean;
  importAvailability: 'AVAILABLE_NOW' | 'READY_TO_IMPORT' | null;
  estimatedArrivalDate: string | null;
}

export const useListingDetails = (id: string | undefined) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState<ListingDetailsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startingChat, setStartingChat] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [similarListings, setSimilarListings] = useState<SimilarListing[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [revealedPhone, setRevealedPhone] = useState<string | null>(null);
  const [revealingPhone, setRevealingPhone] = useState(false);

  const normalizeImage = (img: any): ListingImage => {
    if (typeof img === 'string') {
      return { id: img, url: img, isPrimary: false, alt: '' };
    }
    return {
      id: img.id ?? img.url,
      url: img.url,
      isPrimary: img.isPrimary ?? false,
      alt: img.alt ?? '',
    };
  };

  const fetchListingDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      const response = await listingsService.getById(id);
      const apiData = (response.data || response) as any;

      const transformedData: ListingDetailsData = {
        id: apiData.id || id,
        slug: apiData.slug ?? null,
        title: apiData.title || `${apiData.year} ${apiData.make} ${apiData.model}`,
        make: apiData.make || 'Unknown',
        model: apiData.model || 'Unknown',
        year: apiData.year || new Date().getFullYear(),
        price: parseFloat(apiData.price) || 0,
        originalPrice: apiData.originalPrice ? parseFloat(apiData.originalPrice) : null,
        mileage: apiData.mileage || 0,
        fuelType: apiData.fuelType ?? null,
        transmission: apiData.transmission ?? null,
        bodyType: apiData.bodyType ?? null,
        condition: apiData.condition ?? null,
        color: apiData.color ?? null,
        doors: apiData.doors ?? null,
        seats: apiData.seats ?? null,
        isNegotiable: apiData.isNegotiable ?? true,
        category: apiData.bodyType ?? '',
        location: apiData.location || 'Not specified',
        city: apiData.city ?? null,
        country: apiData.country ?? null,
        description: apiData.description || '',
        features: apiData.features || [],
        images: (apiData.images || []).map(normalizeImage),
        user: apiData.user || { id: '', name: 'Unknown', email: '', phone: null, whatsapp: null },
        dealer: apiData.dealer ?? null,
        status: apiData.status || 'DRAFT',
        viewCount: apiData.viewCount ?? 0,
        favoriteCount: apiData.favoriteCount ?? 0,
        contactCount: apiData.contactCount ?? 0,
        createdAt: apiData.createdAt || apiData.submittedAt || new Date().toISOString(),
        updatedAt: apiData.updatedAt || new Date().toISOString(),
        rankingScore: apiData.rankingScore ?? null,
        priceTag: apiData.priceTag ?? null,
        equipment: apiData.equipment ?? null,
        contact: apiData.contact ?? null,
        isImported: apiData.isImported ?? false,
        importCountry: apiData.importCountry ?? null,
        shippingIncluded: apiData.shippingIncluded ?? true,
        importAvailability: apiData.importAvailability ?? null,
        estimatedArrivalDate: apiData.estimatedArrivalDate ?? null,
      };

      setListing(transformedData);
    } catch (err) {
      console.error('Failed to fetch listing details:', err);
      setError('Failed to load listing details');
      message.error('Failed to load listing details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSimilar = useCallback(async () => {
    if (!id) return;
    setSimilarLoading(true);
    try {
      const result = await listingsService.getSimilar(id);
      setSimilarListings(result);
    } catch {
      // non-critical — silently ignore
    } finally {
      setSimilarLoading(false);
    }
  }, [id]);

  const handleRevealPhone = () => {};

  const handleContactUser = () => {
    if (listing?.user?.phone) {
      window.open(`tel:${listing.user.phone}`, '_self');
    } else if (listing?.user?.email) {
      window.open(`mailto:${listing.user.email}`, '_self');
    } else {
      message.warning('No contact information available');
    }
  };

  const handleToggleFavorite = () => {
    setIsFavorited((prev) => !prev);
    message.success(isFavorited ? 'Removed from saved' : 'Saved to favorites');
  };

  const handleStartChat = async () => {
    if (!listing) return;
    try {
      setStartingChat(true);
      const existingConversations = await chatService.getConversations();
      const existingConversation = existingConversations.find(
        (conv: any) => conv.listingId === listing.id,
      );

      let conversation;
      if (existingConversation) {
        conversation = existingConversation;
        message.info('Opening existing conversation');
      } else {
        conversation = await chatService.getOrCreateConversation(listing.id);
        message.success('Chat started!');
      }
      navigate(`/dashboard/messages?conversation=${conversation.id}`);
    } catch (err: any) {
      console.error('Error starting chat:', err);
      message.error(err.message || 'Failed to start chat');
    } finally {
      setStartingChat(false);
    }
  };

  const handleShare = async (title?: string) => {
    const slug = listing?.slug;
    const url = slug
      ? `${window.location.origin}/listing/${slug}`
      : window.location.href;
    const shareTitle = title || listing?.title || `${listing?.year} ${listing?.make} ${listing?.model}`;
    const text = `${shareTitle} — ${Number(listing?.price).toLocaleString()} ALL | AutoPlace`;

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text, url });
      } catch {
        // user cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        return true; // signal "copied"
      } catch {
        // clipboard not available
      }
    }
    return false;
  };

  useEffect(() => {
    if (id) {
      fetchListingDetails();
      fetchSimilar();
    }
  }, [id]);

  return {
    loading,
    listing,
    error,
    startingChat,
    isFavorited,
    similarListings,
    similarLoading,
    revealedPhone,
    revealingPhone,
    refetch: fetchListingDetails,
    handleContactUser,
    handleRevealPhone,
    handleToggleFavorite,
    handleStartChat,
    handleShare,
  };
};
